import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Package } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { avisoTope, cargarMiSetup } from '@/lib/topeComerciante';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Que va adentro del paquete, con las reglas que dicto Mike el 4-oct
 * (backend/mercaderia.py). Lo usa la compra de etiqueta; la cotizacion de
 * SelleTrans tiene los mismos campos en su formulario.
 *
 * Las categorias, los subtipos, los topes y el telefono de la oficina salen de
 * /insurance/selletrans/config: si cambian, cambian en el servidor y aca no
 * hay que tocar nada.
 */
export const MERCADERIA_VACIA = {
  category: '', subtype: '', description: '', fragile: false, thirdParty: false, code: '',
};

export const categoriaDe = (cfg, codigo) =>
  (cfg?.commodity_categories || []).find(c => c.code === codigo);

/** Lo que falta elegir, o null si esta completo. */
export const faltaMercaderia = (cfg, v) => {
  if (!v.category) return 'Choose what kind of goods are inside the package.';
  if (categoriaDe(cfg, v.category)?.subtypes?.length && !v.subtype) return 'Choose which kind of goods they are.';
  if (!v.description.trim()) return 'Describe what is inside the package.';
  return null;
};

export default function MercaderiaCampos({ value, onChange, destinationCountry = 'US', onConfig }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    fetch(`${API}/insurance/selletrans/config`)
      .then(r => r.json())
      .then(c => { setCfg(c); onConfig && onConfig(c); })
      .catch(() => setCfg({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // El tope propio del comerciante, si tiene setup, para que el aviso diga su numero.
  const { token } = useAuth();
  const [setup, setSetup] = useState(null);
  useEffect(() => { cargarMiSetup(token).then(setSetup); }, [token]);

  const set = (campo, v) => onChange({ ...value, [campo]: v });
  const cat = categoriaDe(cfg, value.category);
  const sub = (cat?.subtypes || []).find(s => s.code === value.subtype);
  const telefono = cfg?.office_phone;

  const avisos = [];
  if (cat && (cat.needs_approval || sub?.needs_approval)) {
    avisos.push(`${sub ? sub.label : cat.label} are covered case by case: call the office at ${telefono} for an approval code.`);
  }
  if (cat && value.fragile) {
    const tope = destinationCountry === 'US' ? cfg.fragile_max_domestic : cfg.fragile_max_international;
    avisos.push(`Fragile items are capped at $${Number(tope).toLocaleString()}. Above that, call the office at ${telefono}.`);
  }
  if (cat && value.fragile) {
    // Mike, 6-oct: solo perdida total, y dos fotos antes de despachar.
    if (cfg.fragile_condition) avisos.push(cfg.fragile_condition);
    if (cfg.fragile_photo_condition) avisos.push(cfg.fragile_photo_condition);
  }
  const tope = avisoTope(cat, setup, telefono);
  if (tope) avisos.push(tope);

  const campo = 'w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm';
  return (
    <Card data-testid="goods-card">
      <CardContent className="pt-6 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
          <Package className="w-3.5 h-3.5" /> What is inside the package?
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <select className={campo} value={value.category} data-testid="goods-category"
            onChange={e => onChange({ ...value, category: e.target.value, subtype: '' })}>
            <option value="">Choose a category…</option>
            {(cfg?.commodity_categories || []).map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
          {cat?.subtypes?.length > 0 && (
            <select className={campo} value={value.subtype} data-testid="goods-subtype"
              onChange={e => set('subtype', e.target.value)}>
              <option value="">Which kind?</option>
              {cat.subtypes.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
          )}
        </div>
        <input className={campo} value={value.description} data-testid="goods-description"
          placeholder="Describe the item, e.g. Rolex Submariner, vintage records, auto parts"
          onChange={e => set('description', e.target.value)} />
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={value.fragile} data-testid="goods-fragile"
              onChange={e => set('fragile', e.target.checked)} /> Fragile or glass
          </label>
        </div>
        {avisos.length > 0 && (
          <p className="text-xs text-amber-600" data-testid="goods-warning">⚠ {avisos.join(' ')}</p>
        )}
        <input className={`${campo} font-mono`} value={value.code} data-testid="goods-code"
          placeholder="Office approval code (if the office gave you one)"
          onChange={e => set('code', e.target.value.toUpperCase())} />
      </CardContent>
    </Card>
  );
}
